// RITA-1585 — Wirksame Darstellung der Handbuch-Leiste (Spalte oder Schublade) aus der VERFÜGBAREN Breite der Zeile,
// in der Inhalt und Handbuch nebeneinander stehen. Gemessen wird vor dem ersten Zeichnen (useLayoutEffect) und danach
// per ResizeObserver an der Zeile (Fenster, Menü auf/zu) – dasselbe Muster wie `useLeistenposition` der
// Platzhalterleiste. Die Regel hängt an der Zeile, nicht an der Fensterbreite.
//
// Verfügbar heißt: Der Seitenrahmen ist auf 1400 px begrenzt und zentriert (`mx: auto`); bei geschlossenem Handbuch
// nehmen seine automatischen Außenabstände den übrigen Platz auf. Die Zeile allein wäre dann zu schmal gemessen und
// das Menü würde zu früh schmal (1920 px: Zeile 1352, verfügbar 1560). Deshalb zählen die aufgelösten Außenabstände
// des Rahmens mit; ohne Begrenzung sind sie 0.
import { useLayoutEffect, useRef, useState } from 'react';
import { wirksameHandbuchdarstellung } from '../model/handbuchplatz';

function aussenabstaende(element: HTMLElement | null): number {
  if (!element || typeof window.getComputedStyle !== 'function') return 0;
  const stil = window.getComputedStyle(element);
  const links = parseFloat(stil.marginLeft);
  const rechts = parseFloat(stil.marginRight);
  return (Number.isFinite(links) ? links : 0) + (Number.isFinite(rechts) ? rechts : 0);
}

export default function useHandbuchPlatz(mindestInhaltBreitePx: number) {
  const zeilenRef = useRef<HTMLDivElement>(null);
  const [zeilenBreite, setZeilenBreite] = useState<number | null>(null);
  /** Stand die Leiste zuletzt als Schublade? Nur dann gilt beim Rückweg zur Spalte der Spielraum. */
  const schubladeRef = useRef(false);

  useLayoutEffect(() => {
    const zeile = zeilenRef.current;
    if (!zeile) return undefined;
    let frame = 0;
    const messen = () => {
      frame = 0;
      // Beim Drucken ist die Leiste ausgeblendet und die Zeile anders breit: letzte Bildschirmmessung behalten.
      if (window.matchMedia?.('print').matches) return;
      const breite = zeile.getBoundingClientRect().width + aussenabstaende(zeile.parentElement);
      setZeilenBreite((alt) => (alt === breite ? alt : breite));
    };
    // Messwerte ändern das Layout. Nicht innerhalb der ResizeObserver-Zustellung schreiben.
    const planen = () => {
      if (!frame) frame = window.requestAnimationFrame(messen);
    };
    messen();
    const beobachter = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(planen);
    beobachter?.observe(zeile);
    // Die Zeile selbst bleibt bei begrenztem Rahmen gleich breit, wenn sich der Platz daneben ändert (Menü auf/zu,
    // erster Aufbau): Deshalb auch den Hauptbereich um den Rahmen beobachten, dessen Breite die Außenabstände bestimmt.
    const hauptbereich = zeile.parentElement?.parentElement;
    if (hauptbereich) beobachter?.observe(hauptbereich);
    window.addEventListener('resize', planen);
    window.addEventListener('afterprint', planen);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      beobachter?.disconnect();
      window.removeEventListener('resize', planen);
      window.removeEventListener('afterprint', planen);
    };
  }, []);

  const darstellung = wirksameHandbuchdarstellung(zeilenBreite, mindestInhaltBreitePx, schubladeRef.current);
  // Nach dem Zeichnen merken, nicht während des Renderns: der Wert zählt erst für die nächste Messung.
  useLayoutEffect(() => {
    schubladeRef.current = darstellung === 'schublade';
  });

  return { zeilenRef, darstellung, zeilenBreite };
}
